// src/pages/admin/ContactInfo.jsx
import React, { useEffect, useState } from "react";
import axios from "axios";
import toast from "react-hot-toast";
import { FiEdit, FiSave, FiTrash2, FiPlus } from "react-icons/fi";
import { confirmAndDelete } from "../../utils/confirmAndDelete";

const ContactInfoSkeleton = () => (
  <div className="bg-white rounded-2xl overflow-hidden border border-gray-200 animate-pulse w-full p-6">
    <div className="space-y-4">
      <div className="h-6 w-48 bg-gray-200 rounded"></div>
      <div className="h-4 bg-gray-200 rounded w-full"></div>
      <div className="h-4 bg-gray-200 rounded w-3/4"></div>
      <div className="h-36 bg-gray-200 rounded"></div>
      <div className="flex justify-end">
        <div className="h-10 w-32 bg-gray-200 rounded"></div>
      </div>
    </div>
  </div>
);

const EmptyState = ({ setEditing }) => (
  <div className="bg-white rounded-3xl p-10 border border-gray-300 text-center w-full">
    <h2 className="text-3xl font-heading font-bold mb-4 text-primaryTextColor bg-gradient-to-r from-primaryBtnColor to-primaryBtnHoverColor bg-clip-text text-transparent">
      Contact Info Not Set
    </h2>
    <p className="text-gray-600 mb-6 text-lg">
      Every strike counts. Add your details so you never miss a shot.
    </p>

    <button
      onClick={() => setEditing(true)}
      className="flex mx-auto items-center justify-center gap-3 bg-gradient-to-r from-primaryBtnColor to-primaryBtnHoverColor text-white px-6 py-3 rounded-full hover:opacity-90 transition transform hover:scale-105"
    >
      <FiPlus size={20} />
      Add Contact Details
    </button>
  </div>
);

const ContactCard = ({
  editing,
  setEditing,
  deleting,
  handleDelete,
  contact,
  handleUpdate,
  handleCreate,
  handleChange,
  form,
  saving,
  setForm,
}) => (
  <div className="bg-white rounded-2xl w-full">
    <div className="flex justify-between items-start mb-6">
      <h2 className="text-2xl font-heading font-bold bg-gradient-to-r from-primaryBtnColor to-primaryBtnHoverColor bg-clip-text text-transparent">
        Contact Details
      </h2>

      <div className="flex flex-col sm:flex-row justify-center gap-2 items-end space-x-2">
        <button
          onClick={() => setEditing((s) => !s)}
          className="flex items-center gap-2 px-4 py-2 rounded-xl shadow-md text-gray-800 bg-gradient-to-r from-gray-100 to-gray-300 hover:from-gray-200 hover:to-gray-400"
          title={editing ? "Cancel Edit" : "Edit Contact"}
        >
          <FiEdit />
          {editing ? "Cancel" : "Edit"}
        </button>

        <button
          onClick={() => handleDelete(contact._id)}
          disabled={deleting}
          className={`flex items-center gap-2 px-4 py-2 rounded-xl shadow-md ${
            deleting
              ? "bg-red-300 text-white cursor-not-allowed"
              : "bg-red-500 text-white hover:bg-red-600"
          }`}
        >
          {deleting ? (
            <svg
              className="w-4 h-4 animate-spin"
              xmlns="http://www.w3.org/2000/svg"
              fill="none"
              viewBox="0 0 24 24"
            >
              <circle
                className="opacity-25"
                cx="12"
                cy="12"
                r="10"
                stroke="currentColor"
                strokeWidth="4"
              ></circle>
              <path
                className="opacity-75"
                fill="currentColor"
                d="M4 12a8 8 0 018-8v4a4 4 0 00-4 4H4z"
              ></path>
            </svg>
          ) : (
            <FiTrash2 />
          )}
          Delete
        </button>
      </div>
    </div>

    <form
      onSubmit={contact ? handleUpdate : handleCreate}
      className="space-y-4"
    >
      <div className="grid md:grid-cols-3 gap-3">
        <div>
          <label className="block text-sm font-medium text-primaryTextColor mb-2">
            Phone Number-1 <span className="italic">(Main)</span>
          </label>
          <input
            name="no1"
            value={form.no1}
            onChange={handleChange}
            disabled={!editing}
            required
            placeholder="e.g- (678) 714-7454"
            className="w-full px-4 py-2 border border-black rounded-lg outline-none focus:ring-1 focus:ring-black"
          />
        </div>
        <div>
          <label className="block text-sm font-medium text-primaryTextColor mb-2">
            Phone Number-2 <span className="italic">(Shots)</span>
          </label>
          <input
            name="no2"
            value={form.no2}
            onChange={handleChange}
            disabled={!editing}
            required
            placeholder="e.g- (678) 287-1100 "
            className="w-full px-4 py-2 border border-black rounded-lg outline-none focus:ring-1 focus:ring-black"
          />
        </div>

        <div>
          <label className="block text-sm font-medium text-primaryTextColor mb-2">
            WhatsApp Number
          </label>

          <input
            name="whatsappNo"
            value={form.whatsappNo}
            onChange={handleChange}
            disabled={!editing}
            required
            placeholder="e.g- (404) 200-2954"
            className="w-full px-4 py-2 border border-black rounded-lg outline-none focus:ring-1 focus:ring-black"
          />
          {editing && (
            <p className="text-xs italic text-gray-400 mt-2">
              ⚡ Make sure this number is ready to strike on WhatsApp!
            </p>
          )}
        </div>
      </div>

      <div>
        <label className="block text-sm font-medium text-primaryTextColor mb-2">
          Email
        </label>
        <input
          name="email"
          value={form.email}
          onChange={handleChange}
          disabled={!editing}
          required
          type="text"
          placeholder="support@company.com"
          className="w-full px-4 py-2 border border-black rounded-lg outline-none focus:ring-1 focus:ring-black"
        />
      </div>

      <div>
        <label className="block text-sm font-medium text-primaryTextColor mb-2">
          Address
        </label>
        {editing && (
          <p className="text-xs italic text-gray-400 mb-2">
            ⚡ For a balanced look, split your address into two short lines.
          </p>
        )}
        <textarea
          name="address"
          value={form.address}
          onChange={handleChange}
          disabled={!editing}
          required
          rows={3}
          placeholder="Office address"
          className="w-full px-4 py-2 border border-black rounded-lg outline-none focus:ring-1 focus:ring-black"
        />
      </div>

      <div>
        <label className="block text-sm font-medium text-primaryTextColor mb-2">
          Chatbot Name
        </label>
        <input
          name="chatbotName"
          value={form.chatbotName}
          onChange={handleChange}
          disabled={!editing}
          required
          placeholder="StrikeBot"
          className="w-full px-4 py-2 border border-black rounded-lg outline-none focus:ring-1 focus:ring-black"
        />
      </div>

      {/* --- NEW: Social Links --- */}
      <div>
        <h3 className="text-lg font-semibold text-primaryTextColor mb-2">
          Social Media Links
        </h3>

        <div className="grid md:grid-cols-3 gap-3">
          <div>
            <label className="block text-sm font-medium text-primaryTextColor mb-2">
              YouTube
            </label>
            <input
              name="youtube"
              value={form.youtube}
              onChange={handleChange}
              disabled={!editing}
              placeholder="https://youtube.com/@yourchannel"
              className="w-full px-4 py-2 border border-black rounded-lg outline-none focus:ring-1 focus:ring-black"
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-primaryTextColor mb-2">
              Facebook
            </label>
            <input
              name="facebook"
              value={form.facebook}
              onChange={handleChange}
              disabled={!editing}
              placeholder="https://facebook.com/yourpage"
              className="w-full px-4 py-2 border border-black rounded-lg outline-none focus:ring-1 focus:ring-black"
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-primaryTextColor mb-2">
              Instagram
            </label>
            <input
              name="instagram"
              value={form.instagram}
              onChange={handleChange}
              disabled={!editing}
              placeholder="https://instagram.com/yourhandle"
              className="w-full px-4 py-2 border border-black rounded-lg outline-none focus:ring-1 focus:ring-black"
            />
          </div>
        </div>
      </div>

      {/* --- END Social Links --- */}
      {/* --- NEW: Business Hours --- */}
      <div>
        <h3 className="text-lg font-semibold text-primaryTextColor mb-2">
          Business Hours
        </h3>

        <div className="grid md:grid-cols-3 gap-3">
          {/* Monday - Friday */}
          <div>
            <label className="block text-sm font-medium text-primaryTextColor mb-1">
              Monday - Friday
            </label>
            <div className="flex gap-2">
              <input
                type="text"
                name="mondayToFridayOpen"
                placeholder="Open"
                value={form.businessHours.mondayToFriday.open}
                onChange={(e) =>
                  setForm((prev) => ({
                    ...prev,
                    businessHours: {
                      ...prev.businessHours,
                      mondayToFriday: {
                        ...prev.businessHours.mondayToFriday,
                        open: e.target.value,
                      },
                    },
                  }))
                }
                disabled={!editing}
                className="w-full px-4 py-2 border border-black rounded-lg"
              />
              <input
                type="text"
                name="mondayToFridayClose"
                placeholder="Close"
                value={form.businessHours.mondayToFriday.close}
                onChange={(e) =>
                  setForm((prev) => ({
                    ...prev,
                    businessHours: {
                      ...prev.businessHours,
                      mondayToFriday: {
                        ...prev.businessHours.mondayToFriday,
                        close: e.target.value,
                      },
                    },
                  }))
                }
                disabled={!editing}
                className="w-full px-4 py-2 border border-black rounded-lg"
              />
            </div>
          </div>

          {/* Saturday */}
          <div>
            <label className="block text-sm font-medium text-primaryTextColor mb-1">
              Saturday
            </label>
            <div className="flex gap-2">
              <input
                type="text"
                placeholder="Open"
                value={form.businessHours.saturday.open}
                onChange={(e) =>
                  setForm((prev) => ({
                    ...prev,
                    businessHours: {
                      ...prev.businessHours,
                      saturday: {
                        ...prev.businessHours.saturday,
                        open: e.target.value,
                      },
                    },
                  }))
                }
                disabled={!editing}
                className="w-full px-4 py-2 border border-black rounded-lg"
              />
              <input
                type="text"
                placeholder="Close"
                value={form.businessHours.saturday.close}
                onChange={(e) =>
                  setForm((prev) => ({
                    ...prev,
                    businessHours: {
                      ...prev.businessHours,
                      saturday: {
                        ...prev.businessHours.saturday,
                        close: e.target.value,
                      },
                    },
                  }))
                }
                disabled={!editing}
                className="w-full px-4 py-2 border border-black rounded-lg"
              />
            </div>
          </div>

          {/* Sunday */}
          <div>
            <label className="block text-sm font-medium text-primaryTextColor mb-1">
              Sunday
            </label>
            <div className="flex gap-2">
              <input
                type="text"
                placeholder="Open"
                value={form.businessHours.sunday.open}
                onChange={(e) =>
                  setForm((prev) => ({
                    ...prev,
                    businessHours: {
                      ...prev.businessHours,
                      sunday: {
                        ...prev.businessHours.sunday,
                        open: e.target.value,
                      },
                    },
                  }))
                }
                disabled={!editing}
                className="w-full px-4 py-2 border border-black rounded-lg"
              />
              <input
                type="text"
                placeholder="Close"
                value={form.businessHours.sunday.close}
                onChange={(e) =>
                  setForm((prev) => ({
                    ...prev,
                    businessHours: {
                      ...prev.businessHours,
                      sunday: {
                        ...prev.businessHours.sunday,
                        close: e.target.value,
                      },
                    },
                  }))
                }
                disabled={!editing}
                className="w-full px-4 py-2 border border-black rounded-lg"
              />
            </div>
          </div>
        </div>
      </div>
      {/* --- END Business Hours --- */}

      <div className="flex justify-end items-center gap-4">
        {editing ? (
          <button
            type="submit"
            disabled={saving}
            className="flex items-center justify-center w-52 gap-2 bg-gradient-to-r from-primaryBtnColor to-primaryBtnHoverColor text-white px-6 py-2 rounded-lg shadow-lg hover:opacity-90 transition disabled:opacity-50"
          >
            {saving ? (
              <svg
                className="w-5 h-5 animate-spin"
                xmlns="http://www.w3.org/2000/svg"
                fill="none"
                viewBox="0 0 24 24"
              >
                <circle
                  className="opacity-25"
                  cx="12"
                  cy="12"
                  r="10"
                  stroke="currentColor"
                  strokeWidth="4"
                ></circle>
                <path
                  className="opacity-75"
                  fill="currentColor"
                  d="M4 12a8 8 0 018-8v4a4 4 0 00-4 4H4z"
                ></path>
              </svg>
            ) : (
              <FiSave size={18} />
            )}
            {saving ? "Saving..." : "Save Changes"}
          </button>
        ) : null}
      </div>
    </form>
  </div>
);

const ContactInfo = () => {
  const [contact, setContact] = useState(null);
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState(false);
  const [saving, setSaving] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const [form, setForm] = useState({
    no1: "",
    no2: "",
    whatsappNo: "",
    email: "",
    address: "",
    chatbotName: "",
    youtube: "",
    facebook: "",
    instagram: "",
    businessHours: {
      mondayToFriday: { open: "", close: "" },
      saturday: { open: "", close: "" },
      sunday: { open: "", close: "" },
    },
  });

  const base = process.env.REACT_APP_API_URL;

  const fetchContact = async () => {
    try {
      setLoading(true);
      const res = await axios.get(`${base}/contactinfo/get-contact`);
      if (res.data?.data) {
        setContact(res.data.data);
        setForm({
          no1: res.data.data.no1 || "",
          no2: res.data.data.no2 || "",
          whatsappNo: res.data.data.whatsappNo || "",
          email: res.data.data.email || "",
          address: res.data.data.address || "",
          chatbotName: res.data.data.chatbotName || "",
          youtube: res.data.data.socialLinks?.youtube || "",
          facebook: res.data.data.socialLinks?.facebook || "",
          instagram: res.data.data.socialLinks?.instagram || "",
          businessHours: {
            mondayToFriday: {
              open: res.data.data.businessHours?.mondayToFriday?.open || "",
              close: res.data.data.businessHours?.mondayToFriday?.close || "",
            },
            saturday: {
              open: res.data.data.businessHours?.saturday?.open || "",
              close: res.data.data.businessHours?.saturday?.close || "",
            },
            sunday: {
              open: res.data.data.businessHours?.sunday?.open || "",
              close: res.data.data.businessHours?.sunday?.close || "",
            },
          },
        });
      } else {
        setContact(null);
      }
    } catch (err) {
      if (err.response?.status !== 404)
        console.error("Fetch contact error:", err);
      setContact(null);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchContact();
  }, []);

  const handleChange = (e) =>
    setForm((s) => ({ ...s, [e.target.name]: e.target.value }));

  const handleCreate = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      const payload = {
        ...form,
        socialLinks: {
          youtube: form.youtube,
          facebook: form.facebook,
          instagram: form.instagram,
        },
        businessHours: form.businessHours,
      };
      const res = await axios.post(`${base}/contactinfo/create`, payload);
      toast.success("Contact info created");
      setContact(res.data.data);
      setEditing(false);
    } catch (err) {
      console.error("Create error:", err);
      toast.error(err.response?.data?.message || "Could not create contact");
    } finally {
      setSaving(false);
    }
  };

  const handleUpdate = async (e) => {
    e.preventDefault();
    if (!contact?._id) return;
    setSaving(true);
    try {
      const payload = {
        ...form,
        socialLinks: {
          youtube: form.youtube,
          facebook: form.facebook,
          instagram: form.instagram,
        },
        businessHours: form.businessHours,
      };
      const res = await axios.put(
        `${base}/contactinfo/update/${contact._id}`,
        payload
      );
      toast.success("Contact info updated");
      setContact(res.data.data);
      setEditing(false);
    } catch (err) {
      console.error("Update error:", err);
      toast.error(err.response?.data?.message || "Could not update contact");
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (id) => {
    const isConfirmed = await confirmAndDelete({
      id,
      url: `${process.env.REACT_APP_API_URL}/contactinfo/delete`,
    });

    if (!isConfirmed) return;

    try {
      setDeleting(true);
      toast.success("Contact info deleted");
      setContact(null);
      setForm({
        no1: "",
        no2: "",
        whatsappNo: "",
        email: "",
        address: "",
        chatbotName: "",
        youtube: "",
        facebook: "",
        instagram: "",
      });
      setEditing(false);
    } catch (err) {
      console.error("Delete error:", err);
      toast.error("Failed to delete contact info");
    } finally {
      setDeleting(false);
    }
  };

  return (
    <div className="min-h-screen bg-transparent p-6 w-full">
      <h1 className="text-3xl font-heading font-bold mb-8 bg-gradient-to-r from-primaryBtnColor to-primaryBtnHoverColor bg-clip-text text-transparent inline-block">
        Manage Contact Information
      </h1>

      {loading ? (
        <ContactInfoSkeleton />
      ) : contact ? (
        <ContactCard
          contact={contact}
          form={form}
          editing={editing}
          saving={saving}
          deleting={deleting}
          setForm={setForm}
          setEditing={setEditing}
          handleChange={handleChange}
          handleCreate={handleCreate}
          handleUpdate={handleUpdate}
          handleDelete={handleDelete}
        />
      ) : (
        <EmptyState setEditing={setEditing} />
      )}
    </div>
  );
};

export default ContactInfo;
